import React from "react";
import { useMediaQuery } from "react-responsive";

// DropGap component for visual gap during drag
function DropGap({
  isActive,
  onDragOver,
  onDrop,
  onDragLeave,
  darkMode,
}: {
  isActive: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  darkMode?: boolean;
}) {
  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      style={{
        display: "inline-block",
        width: isActive ? 36 : 8,
        height: 40,
        marginLeft: 2,
        marginRight: 2,
        background: isActive
          ? darkMode
            ? "#334155" // slate-800
            : "#c7d2fe" // blue-200
          : "transparent",
        borderRadius: 8,
        transition: "all 0.1s",
        verticalAlign: "middle",
      }}
    />
  );
}

function splitWordsToRowsByWidth(
  words: string[],
  rowCount = 3,
  rowWidth = 700
) {
  const rows: string[][] = Array.from({ length: rowCount }, () => []);
  let currentRow = 0;
  let currentWidth = 0;
  const gap = 8;
  words.forEach((word) => {
    const estWidth = 32 + 8 + word.length * 10;
    if (currentWidth + estWidth > rowWidth && currentRow < rowCount - 1) {
      currentRow++;
      currentWidth = 0;
    }
    rows[currentRow].push(word);
    currentWidth += estWidth + gap;
  });
  return rows;
}

const ROW_HEIGHT = 56;

interface ComposeSentenceAnswerAreaProps {
  selectedWords: string[];
  setSelectedWords: (words: string[]) => void;
  draggedWord: string | null;
  setDraggedWord: (word: string | null) => void;
  draggedFromSentenceIdx: number | null;
  setDraggedFromSentenceIdx: (idx: number | null) => void;
  hoverIdx: number | null;
  setHoverIdx: (idx: number | null) => void;
  checked?: boolean;
  isCorrect?: boolean | null;
  correctAnswerIds: (number | null)[];
  wordBank: { id: number; indonesia: string }[];
  darkMode?: boolean;
}

const ComposeSentenceAnswerArea: React.FC<ComposeSentenceAnswerAreaProps> = ({
  selectedWords,
  setSelectedWords,
  draggedWord,
  setDraggedWord,
  draggedFromSentenceIdx,
  setDraggedFromSentenceIdx,
  hoverIdx,
  setHoverIdx,
  checked,
  isCorrect,
  correctAnswerIds,
  wordBank,
  darkMode = false,
}) => {
  // Remove word from sentence area (by clicking)
  const handleRemoveSelectedWord = (idx: number) => {
    setSelectedWords(selectedWords.filter((_, i) => i !== idx));
  };

  // Drag from sentence area
  const handleDragStartSentence = (word: string, idx: number) => {
    setDraggedWord(word);
    setDraggedFromSentenceIdx(idx);
  };

  // Drop to sentence area (append or reorder)
  const handleDropSentence = (dropIdx: number | null) => {
    if (!draggedWord) return;
    let newSentence = [...selectedWords];
    // Remove if dragging from sentence area (reorder)
    if (draggedFromSentenceIdx !== null) {
      newSentence.splice(draggedFromSentenceIdx, 1);
      // Adjust dropIdx if removing from before drop position
      if (draggedFromSentenceIdx < (dropIdx ?? newSentence.length)) {
        if (dropIdx !== null) dropIdx -= 1;
      }
    }
    // Insert at dropIdx or at end
    if (dropIdx === null || dropIdx > newSentence.length) {
      newSentence.push(draggedWord);
    } else {
      newSentence.splice(dropIdx, 0, draggedWord);
    }
    setSelectedWords(newSentence);
    setDraggedWord(null);
    setDraggedFromSentenceIdx(null);
    setHoverIdx(null);
  };

  // Map Indonesian word to id for feedback coloring
  const getWordId = (word: string) => {
    const found = wordBank.find((w) => w.indonesia === word);
    return found ? found.id : null;
  };

  // Responsive row width for wrapping
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const answerRows = splitWordsToRowsByWidth(
    selectedWords,
    isMobile ? 4 : 3,
    isMobile ? 320 : 700
  );

  return (
    <div className="flex flex-col items-center w-full">
      <div
        className="w-full max-w-3xl relative"
        style={{
          minHeight: `${ROW_HEIGHT * (isMobile ? 4 : 3)}px`,
          paddingTop: "0px",
          paddingBottom: "0px",
          maxWidth: "100vw",
          overflowX: "hidden",
        }}
      >
        {/* Top, middle, and bottom lines */}
        <div
          style={{ position: "absolute", left: 0, right: 0, top: 0, zIndex: 0 }}
        >
          <div
            className={`w-full h-px ${
              darkMode ? "bg-gray-600" : "bg-gray-400"
            }`}
          />
        </div>
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `${ROW_HEIGHT}px`,
            zIndex: 0,
          }}
        >
          <div
            className={`w-full h-px ${
              darkMode ? "bg-gray-600" : "bg-gray-400"
            }`}
          />
        </div>
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `${ROW_HEIGHT * 2}px`,
            zIndex: 0,
          }}
        >
          <div
            className={`w-full h-px ${
              darkMode ? "bg-gray-600" : "bg-gray-400"
            }`}
          />
        </div>
        {/* Add extra line for mobile mode */}
        {isMobile && (
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${ROW_HEIGHT * 3}px`,
              zIndex: 0,
            }}
          >
            <div
              className={`w-full h-px ${
                darkMode ? "bg-gray-600" : "bg-gray-400"
              }`}
            />
          </div>
        )}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
          }}
        >
          <div
            className={`w-full h-px ${
              darkMode ? "bg-gray-600" : "bg-gray-400"
            }`}
          />
        </div>
        {/* The answer area: 3 rows, each centered */}
        <div style={{ position: "relative", zIndex: 1 }}>
          {answerRows.map((row, rowIdx) => {
            const rowStartIdx = answerRows
              .slice(0, rowIdx)
              .reduce((a, r) => a + r.length, 0);
            const isFirstRow = rowIdx === 0;

            return (
              <div
                key={rowIdx}
                className={
                  isMobile
                    ? "flex gap-1 justify-center items-center flex-wrap"
                    : "flex gap-2 justify-center items-center flex-wrap"
                }
                style={{
                  minHeight: `${ROW_HEIGHT}px`,
                  height: "auto",
                  width: "100%",
                  flexWrap: "wrap",
                  overflow: "visible",
                }}
              >
                {/* Top, middle, and bottom lines */}
                {draggedWord && isFirstRow && (
                  <DropGap
                    isActive={hoverIdx === 0}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setHoverIdx(0);
                    }}
                    onDragLeave={(e) => {
                      if (hoverIdx === 0) setHoverIdx(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDropSentence(0);
                    }}
                    darkMode={darkMode}
                  />
                )}
                {row.map((word, idx) => {
                  const globalIdx = rowStartIdx + idx;
                  // Only shift the two boxes directly next to the drop gap
                  const shiftLeft =
                    draggedWord &&
                    hoverIdx === globalIdx + 1 &&
                    idx === row.length - 1
                      ? false
                      : draggedWord && hoverIdx === globalIdx + 1;
                  const shiftRight =
                    draggedWord && hoverIdx === globalIdx && idx === 0
                      ? false
                      : draggedWord && hoverIdx === globalIdx;
                  // Feedback logic:
                  let feedbackClass = "";
                  if (checked) {
                    const wordId = getWordId(word);
                    if (wordId === correctAnswerIds[globalIdx]) {
                      feedbackClass = darkMode
                        ? "bg-lime-800/90 border-lime-400 text-lime-200"
                        : "bg-lime-100 border-lime-500 text-lime-700";
                    } else {
                      feedbackClass = darkMode
                        ? "bg-red-800/90 border-red-400 text-red-200"
                        : "bg-red-100 border-red-500 text-red-700";
                    }
                  } else {
                    feedbackClass = darkMode
                      ? "bg-blue-900 border-blue-400 text-blue-200 hover:bg-blue-800"
                      : "bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200";
                  }
                  return (
                    <React.Fragment key={word + globalIdx}>
                      <div
                        className="relative transition-transform duration-100"
                        draggable={!checked}
                        onDragStart={
                          !checked
                            ? () => handleDragStartSentence(word, globalIdx)
                            : undefined
                        }
                        onDragEnd={() => {
                          setHoverIdx(null);
                          setDraggedWord(null);
                          setDraggedFromSentenceIdx(null);
                        }}
                        onClick={() => handleRemoveSelectedWord(globalIdx)}
                        style={{
                          cursor: !checked
                            ? draggedWord
                              ? "grabbing"
                              : "grab"
                            : "default",
                          transform:
                            shiftLeft && hoverIdx === globalIdx + 1
                              ? "translateX(-18px)"
                              : shiftRight && hoverIdx === globalIdx
                              ? "translateX(18px)"
                              : "none",
                          zIndex: shiftLeft || shiftRight ? 2 : 1,
                          opacity:
                            draggedWord === word &&
                            draggedFromSentenceIdx === globalIdx
                              ? 0.5
                              : 1,
                          pointerEvents: checked ? "none" : "auto", // Optional: also disable click
                        }}
                      >
                        <button
                          className={`px-4 py-2 border rounded-lg transition-colors ${feedbackClass}`}
                          type="button"
                          style={{
                            minWidth: "40px",
                            minHeight: "40px",
                            whiteSpace: "nowrap",
                            pointerEvents: "none",
                            fontFamily: "'DIN Next Rounded'",
                          }}
                          tabIndex={-1}
                        >
                          {word}
                        </button>
                      </div>
                      {/* DropGap between words */}
                      {draggedWord && idx < row.length - 1 && (
                        <DropGap
                          isActive={hoverIdx === globalIdx + 1}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setHoverIdx(globalIdx + 1);
                          }}
                          onDragLeave={(e) => {
                            if (hoverIdx === globalIdx + 1) setHoverIdx(null);
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            handleDropSentence(globalIdx + 1);
                          }}
                          darkMode={darkMode}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
                {/* DropGap at the very end of each row */}
                {draggedWord && row.length > 0 && (
                  <DropGap
                    isActive={hoverIdx === rowStartIdx + row.length}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setHoverIdx(rowStartIdx + row.length);
                    }}
                    onDragLeave={(e) => {
                      if (hoverIdx === rowStartIdx + row.length)
                        setHoverIdx(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDropSentence(rowStartIdx + row.length);
                    }}
                    darkMode={darkMode}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ComposeSentenceAnswerArea;
